import { createAnalyticsSettingsLegacy } from '../common/schemas/legacy/analytics';
import { createExtendedPositionLegacy } from '../common/schemas/legacy/extended-position';
import { createThemeButtonDataLegacy, createThemeButtonHoverLegacy } from '../common/schemas/legacy/theme-button';
import { createWidgetPositionLegacy } from '../common/schemas/legacy/widget-position';

import { createLoginEduCourseTariffDetailsLegacy } from './schemas/legacy/course';

import type {
    LegacyLoginEduDefault,
    LegacyLoginEduWidget,
    LegacyLoginEduZero,
    LoginEduAuthorizationButtonLegacy,
    LoginEduCheckboxLegacy,
    LoginEduFormTriggerLegacy,
    LoginEduInnerButtonLegacy,
    LoginEduRegistrationButtonLegacy,
    LoginEduWidgetEntity,
} from './schemas';

/**
 * Host-only values the package deliberately doesn't own — i18n strings and the
 * default button-icon SVG template. The host model passes them in, mirroring
 * the form/payment-form widgets' overrides convention.
 */
export type CreateLegacyLoginEduWidgetOptions = {
    texts?: {
        /** `control_authorization` */
        authorizationLabel?: string;
        /** `control_registration` */
        registrationLabel?: string;
        /** `control_enroll` */
        enrollLabel?: string;
        /** `control_enter_course` */
        enterCourseLabel?: string;
        /** `control_registration_checkbox` */
        checkboxLabel?: string;
        /** `control_edu_registration_lead_sourse` */
        analyticsLeadSource?: string;
    };
    /** Default icon SVG (`DEFAULT_BUTTON_ICON.icon` in the host). */
    buttonIcon?: string;
};

/**
 * `DEFAULT_BUTTON_ICON` / `DEFAULT_BUTTON_ADDITIONAL_EFFECTS` from the host's
 * `base-button.const.ts`, minus the icon SVG (host-supplied).
 */
const createTriggerExtras = (icon: string) =>
    ({
        buttonIcon: {
            isActive: false,
            icon,
            position: 'L',
        },
        additionalEffects: {
            isActive: false,
            effectCondition: 'hover',
            hoverEffects: 'bounce',
            staticEffects: 'shine',
        },
    }) as const;

const createAdvancedRadius = () => ({
    borderBottomLeftRadius: null,
    borderBottomRightRadius: null,
    borderTopLeftRadius: null,
    borderTopRightRadius: null,
});

/**
 * The button that opens the auth modal when `formView` is `'modal'`. Both auth
 * buttons seed the same trigger shape, differing only in label and style
 * preset — exactly what the host's `buildTriggerFrom*` backfill produced.
 */
const createFormTrigger = (
    label: string,
    styleType: 'primary' | 'secondary',
    icon: string
): LoginEduFormTriggerLegacy => ({
    isActive: true,
    label,
    styleType,
    size: 'M',
    buttonBase: createThemeButtonDataLegacy(),
    buttonHover: createThemeButtonHoverLegacy(),
    widthType: 'auto',
    horizontalFormWidthType: 'auto',
    widthValue: 260,
    ...createTriggerExtras(icon),
    isIndependentCorners: false,
    advancedRadius: createAdvancedRadius(),
    advancedBorderSettings: null,
    isShadow: false,
    shadowSettings: null,
});

/** Inner button of the registration group (register / enroll / enter course). */
const createInnerButton = (label: string): LoginEduInnerButtonLegacy => ({
    label,
    styleType: 'secondary',
    size: 'M',
    buttonBase: createThemeButtonDataLegacy(),
    buttonActive: null,
    buttonHover: null,
    isShadow: null,
    shadowSettings: null,
    isIndependentCorners: null,
    advancedRadius: null,
    advancedBorderSettings: null,
    isActive: null,
});

const createAuthorizationButton = (
    label: string,
    triggerLabel: string,
    icon: string
): LoginEduAuthorizationButtonLegacy => ({
    buttonType: 'authorization',
    label,
    styleType: 'primary',
    size: 'M',
    isActive: true,
    buttonBase: createThemeButtonDataLegacy(),
    buttonActive: null,
    buttonHover: createThemeButtonHoverLegacy(),
    isIndependentCorners: false,
    advancedRadius: createAdvancedRadius(),
    advancedBorderSettings: null,
    isShadow: false,
    shadowSettings: null,
    modalTrigger: createFormTrigger(triggerLabel, 'primary', icon),
});

const createRegistrationButton = (
    registrationLabel: string,
    enrollLabel: string,
    enterCourseLabel: string,
    icon: string
): LoginEduRegistrationButtonLegacy => ({
    buttonType: 'registration',
    isActive: true,
    registrationButton: createInnerButton(registrationLabel),
    enrollButton: createInnerButton(enrollLabel),
    enterButton: createInnerButton(enterCourseLabel),
    buttonBase: createThemeButtonDataLegacy(),
    buttonHover: createThemeButtonHoverLegacy(),
    isIndependentCorners: false,
    advancedRadius: createAdvancedRadius(),
    advancedBorderSettings: null,
    isShadow: false,
    shadowSettings: null,
    modalTrigger: createFormTrigger(registrationLabel, 'secondary', icon),
});

const createConsentCheckbox = (label: string): LoginEduCheckboxLegacy => ({
    buttonType: 'checkbox',
    label,
    crmContactVariable: null,
    crmVariableType: null,
    required: false,
});

const createEmailElement = () => ({
    type: 'email' as const,
    label: 'Email',
    placeholder: 'username@gmail.com',
    required: true,
    protected: true,
    isDynamicPlaceholderEnabled: false,
    crmContactVariable: null,
    crmDealVariable: null,
    variable: 'email',
    htmlInputType: 'email' as const,
    autocompleteType: 'email' as const,
});

const createPasswordElement = () => ({
    type: 'password' as const,
    label: 'Password',
    placeholder: '********',
    required: true,
    protected: true,
    isDynamicPlaceholderEnabled: false,
    crmContactVariable: null,
    crmDealVariable: null,
    variable: null,
    htmlInputType: 'password' as const,
    autocompleteType: 'off' as const,
});

/**
 * Field values shared by both placement variants — everything except
 * `position` and the zero-only `isSynchronizeStyle`.
 */
const createCommonData = (
    options?: CreateLegacyLoginEduWidgetOptions
): Omit<LegacyLoginEduDefault['data'], 'position'> => {
    const texts = options?.texts ?? {};
    const icon = options?.buttonIcon ?? '';

    return {
        course: null,
        courseTariffId: null,
        courseTariffDetails: createLoginEduCourseTariffDetailsLegacy(),

        buttons: [
            createAuthorizationButton(
                texts.authorizationLabel ?? 'Authorization',
                texts.authorizationLabel ?? 'Authorization',
                icon
            ),
            createRegistrationButton(
                texts.registrationLabel ?? 'Registration',
                texts.enrollLabel ?? 'Enroll',
                texts.enterCourseLabel ?? 'Enter course',
                icon
            ),
        ],
        checkboxes: [createConsentCheckbox(texts.checkboxLabel ?? '')],
        registrationCheckbox: null,

        signUpElements: [createEmailElement()],
        signInElements: [createEmailElement(), createPasswordElement()],

        horizontalPosition: 'L',
        formView: 'modal',
        defaultForm: 'register',
        linkBetweenForms: true,
        inputSettings: {
            showLabels: true,
            inputBorderType: 'fullBorder',
            formStyleType: 'vertical',
            backgroundColor: '#fff',
            borderColor: '#ddd',
            textColor: '#333',
            inputTextColor: '#333',
            borderRadius: 3,
            inputSize: 'M',
            horizontalPosition: 'L',
            linkColor: '#333',
            modalBackgroundColor: '#fbfcfc',
            modalBorderRadius: 6,
            modalPadding: 30,
        },
        additionalFormSettings: {
            actionAfterConfirmType: 'link',
        },
        analyticsSettings: createAnalyticsSettingsLegacy({
            googleSettings: {
                category: null,
                action: null,
                label: null,
                currency: null,
                value: null,
                leadSource: texts.analyticsLeadSource ?? null,
            },
        }),

        hideOnMobile: false,
        hideOnTablet: false,
        hideOnDesktop: false,
    };
};

const createDefaultData = (options?: CreateLegacyLoginEduWidgetOptions): LegacyLoginEduDefault['data'] => ({
    ...createCommonData(options),
    position: createWidgetPositionLegacy({
        x: 0,
        y: 0,
        zIndex: 0,
        rotate: 0,
        mobileX: 0,
        mobileY: 0,
        mobileZIndex: 0,
        mobileRotate: 0,
        width: 100,
    }),
});

const createZeroData = (options?: CreateLegacyLoginEduWidgetOptions): LegacyLoginEduZero['data'] => ({
    ...createCommonData(options),
    // Zero blocks are laid out per-device — start with sync off (form/button precedent).
    isSynchronizeStyle: false,
    // Height is a placeholder: the login-edu view DOM-syncs the rendered content
    // height into the grid (the button/text `setupZeroHeightSync` pattern).
    position: createExtendedPositionLegacy({
        width: 400,
        tabletWidth: 400,
        mobileWidth: 320,
        height: 360,
        tabletHeight: 360,
        mobileHeight: 360,
    }),
});

export type LegacyLoginEduWidgetVariant = 'default' | 'zero';

/**
 * Factory for the current legacy login-edu entity — matches the persisted
 * widget JSON shape (`{ id, name: 'login-edu', columnId, data, … }`) and the
 * host model's `loginEduData()` defaults. Variant selects between
 * default-column placement (no `type` field, simple position grid) and
 * zero-block placement (`type: 'zero'`, extended-position grid).
 *
 * The host's old `dataAdapter` backfilled `modalTrigger` onto auth buttons that
 * predated it (`buildTriggerFromAuthorization` / `buildTriggerFromRegistration`);
 * signal-form widgets have no `dataAdapter`, so that role now belongs to
 * `WebLegacyWidgetSignalFormService`'s default-backfill over this factory.
 */
export function createLegacyLoginEduWidget(
    variant?: 'default',
    options?: CreateLegacyLoginEduWidgetOptions
): LegacyLoginEduDefault;
export function createLegacyLoginEduWidget(
    variant: 'zero',
    options?: CreateLegacyLoginEduWidgetOptions
): LegacyLoginEduZero;
export function createLegacyLoginEduWidget(
    variant: LegacyLoginEduWidgetVariant = 'default',
    options?: CreateLegacyLoginEduWidgetOptions
): LegacyLoginEduWidget {
    if (variant === 'zero') {
        return {
            id: '',
            name: 'login-edu',
            columnId: '',
            type: 'zero',
            data: createZeroData(options),
        };
    }

    return {
        id: '',
        name: 'login-edu',
        columnId: '',
        data: createDefaultData(options),
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default
 * for new login-edu widgets. While only `legacy` exists, this delegates to
 * {@link createLegacyLoginEduWidget}; pass the variant to opt into the
 * zero-block tag.
 */
export function createLoginEduWidget(
    variant?: 'default',
    options?: CreateLegacyLoginEduWidgetOptions
): LegacyLoginEduDefault;
export function createLoginEduWidget(variant: 'zero', options?: CreateLegacyLoginEduWidgetOptions): LegacyLoginEduZero;
export function createLoginEduWidget(
    variant: LegacyLoginEduWidgetVariant = 'default',
    options?: CreateLegacyLoginEduWidgetOptions
): LoginEduWidgetEntity {
    return variant === 'zero'
        ? createLegacyLoginEduWidget('zero', options)
        : createLegacyLoginEduWidget('default', options);
}
